/**
 * Enable-edge audio heuristics — pure; never write freeze/rechoose.
 */

import type { SimParams } from "../sim/params";
import {
  defaultAudioParams,
  validateAudioParams,
  type AudioParams,
} from "./params";

export type AudioDefaultsContext = {
  /** CamelCase keys present in the partial being resolved (preset/UI explicit). */
  explicit?: ReadonlySet<string>;
  sim: Pick<SimParams, "n" | "gravityMode" | "freezeTopology" | "topology">;
};

/** Auto particle stride so extract samples stay ~≤600. */
export function strideForN(n: number): number {
  if (n <= 400) return 1;
  if (n <= 1200) return 2;
  if (n <= 2500) return 4;
  return Math.max(4, Math.ceil(n / 600));
}

/**
 * Resolve audio bag when enabling or applying a teach preset.
 * Only fills keys not in `explicit`. Never mutates sim freeze/rechoose.
 */
export function resolveAudioDefaults(
  partial: Partial<AudioParams>,
  ctx: AudioDefaultsContext,
): AudioParams {
  const draft = { ...defaultAudioParams(), ...partial };
  const ex = ctx.explicit ?? new Set<string>();

  if (draft.particleStride === 0) {
    draft.particleStride = strideForN(ctx.sim.n);
  }

  if (!ex.has("gravityVoice")) {
    draft.gravityVoice = ctx.sim.gravityMode !== "none";
  }
  if (!ex.has("topologyVoice")) {
    draft.topologyVoice = !ctx.sim.freezeTopology;
  }
  return validateAudioParams(draft);
}
